import type { InfoGroup } from '#components/tabularOverlay/InfoEntry'

const maxDesiredWeightByColumn = 10
const maxColumns = 3
const entryWeight = 1
const groupWeight = 2

const sumWeight = (a: number, b: { weight: number }) => a + b.weight

/* Split the entries into all possible combinations, by splicing between the entries, returning the indices of the entries */
function splitCombinations<T>(entries: T[], splitCount: number): T[][][] {
  const combinations: T[][][] = []

  if (splitCount === 1) {
    return [[entries]]
  }

  // recursively split the entries
  for (let i = 1; i < entries.length; i++) {
    const left = entries.slice(0, i)
    const right = entries.slice(i)

    const rightCombinations = splitCombinations(right, splitCount - 1)
    for (const rightCombination of rightCombinations) {
      combinations.push([left, ...rightCombination])
    }
  }

  return combinations
}

function rateSplit(split: { weight: number }[][]): number {
  const weights = split.map((group) => group.reduce(sumWeight, 0))
  const minWeight = Math.min(...weights)
  const maxWeight = Math.max(...weights)
  const balance = minWeight / maxWeight

  // penalize the split if the max weight is too high
  if (maxWeight > maxDesiredWeightByColumn) {
    return balance - (maxWeight - maxDesiredWeightByColumn) / maxDesiredWeightByColumn
  }
  // prefer two columns over three
  if (split.length === 2) {
    return balance + 0.1
  }

  return balance
}

/* Split the info groups into multiple columns */
export function balanceEntries(infoGroups: InfoGroup[]): InfoGroup[][] {
  const groupWeights = infoGroups.map((group, index) => {
    return { index, weight: group.entries.length * entryWeight + groupWeight }
  })

  let bestSplit: (typeof groupWeights)[] | undefined
  let bestSplitRating = Number.NEGATIVE_INFINITY

  for (let columnCount = 1; columnCount <= maxColumns; columnCount++) {
    const splits = splitCombinations(groupWeights, columnCount)

    for (const split of splits) {
      const rating = rateSplit(split)
      if (rating > bestSplitRating) {
        bestSplitRating = rating
        bestSplit = split
      }
    }
  }

  if (!bestSplit) {
    return [infoGroups]
  }

  // map indices back to groups
  return bestSplit.map((group) => group.map(({ index }) => infoGroups[index]))
}
